import { RouteComponentProps, Router } from "@reach/router"
import { FullPageLoader, ScrollToTop } from "@shared/primitive-components"
import React, { useMemo, useEffect, useState } from "react"
import { createAccessService } from "./auth/api/accessService"
import { AuthenticatedRouter } from "./AuthenticatedRouter"
import { ViewerProvider } from "./broker_src/auth/ui/ViewerProvider"
import BrokerScreen from "./broker_src/components/Screen/Screen"
import { PATHS as BROKER_PATHS } from "./broker_src/const"
import { PolicyLookupRouter } from "./broker_src/lookupPolicy/PolicyLookupRouter"
import { PoliciesRouter } from "./broker_src/policies/ui/PoliciesRouter"
import { ErrorBoundary } from "./components/ErrorBoundary"
import Screen from "./components/Screen"
import { PATHS } from "./const/paths"
import { AccessProvider, PolicyAndFleetProvider, UiProvider, useAuth } from "./contexts"
import { PolicyDevTools } from "./integration"
import { ClaimSubmissions } from "./integration/ClaimSubmissions"
import { IFrame } from "./integration/iframe"
import { BrokerClaims } from "./pages/Claims/BrokerClaims"
import { CreateClaimSubmission } from "./pages/CreateClaimSubmission"
import { Metrics } from "./pages/Metrics/Metrics"
import { ReferralsRouter } from "./pages/Referrals/ReferralsRouter"
import { GlobalOverflowHidden } from "./pages/styles/UnauthStyles"

const CREATE_CLAIM_SUBMISSION_PATH_REGEX = new RegExp(`^/${PATHS.CREATE_CLAIM_SUBMISSION}$`)
const CLAIM_SUBMISSION_PATH_REGEX = new RegExp(`^/${PATHS.CLAIM_SUBMISSIONS.BASE}/[^/]+$`)
const CLAIM_SUBMISSIONS_REVIEW_PATH_REGEX = new RegExp(
  `^/policy/[^/]+/${PATHS.CLAIM_SUBMISSIONS.BASE}/[^/]+/${PATHS.CLAIM_SUBMISSIONS.REVIEW}$`
)

export const Authenticated: React.FC<RouteComponentProps> = () => {
  const authService = useAuth()

  const user = useMemo(() => authService.getAuthUser(), [authService])
  const isFlocker = useMemo(() => user?.type === `admin`, [user])
  const isClaimsAdmin = useMemo(() => user?.type === `claims_admin`, [user])
  const isCarrier = useMemo(() => user?.type === `carrier`, [user])
  const isNotLive = useMemo(() => !window.location.href.includes(`portal.flockcover.com`), [])

  const isCreateClaimSubmissionPath = useMemo(
    () => CREATE_CLAIM_SUBMISSION_PATH_REGEX.test(window.location.pathname),
    []
  )
  const isClaimSubmissionPath = useMemo(() => CLAIM_SUBMISSION_PATH_REGEX.test(window.location.pathname), [])
  const isClaimSubmissionReviewPath = useMemo(
    () => CLAIM_SUBMISSIONS_REVIEW_PATH_REGEX.test(window.location.pathname),
    []
  )

  const [policyId] = useState<string | undefined>(
    window.location.pathname.replace(`/policy/`, ``).split(`/`)[0] || undefined
  )

  useEffect(() => {
    if (!authService.isAuthenticated && !authService.isLoading) {
      ;(async () => authService.login({ email: ``, password: `` }))()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authService])

  const renderClaimSubmission = () => {
    const secret = window.location.pathname.split(`/${PATHS.CLAIM_SUBMISSIONS.BASE}/`)[1]
    return (
      <>
        <GlobalOverflowHidden />
        <IFrame path={`${PATHS.CLAIM_SUBMISSIONS.BASE}/${secret}`} />
      </>
    )
  }

  const renderClaimSubmissionReview = () => {
    const submissionReference = window.location.pathname.split(`/`).slice(-2)[0]
    return (
      <AccessProvider accessService={createAccessService(authService)}>
        <PolicyAndFleetProvider policyId={policyId} ownerId={user?.ownerId ?? undefined}>
          <ClaimSubmissions submissionReference={submissionReference} />
        </PolicyAndFleetProvider>
      </AccessProvider>
    )
  }

  const renderBrokerView = () => (
    <ViewerProvider>
      <BrokerScreen>
        <Router>
          <ScrollToTop path="/">
            <Metrics default />
            <PoliciesRouter path={`/${BROKER_PATHS.POLICIES}/*`} />
            {(isFlocker || isClaimsAdmin || isCarrier) && (
              <PolicyLookupRouter path={`/${BROKER_PATHS.POLICY_LOOKUP}/*`} />
            )}
            <BrokerClaims path={`/${BROKER_PATHS.CLAIMS}/*`} />
          </ScrollToTop>
        </Router>
      </BrokerScreen>
    </ViewerProvider>
  )

  const renderCustomerView = () => (
    <AccessProvider accessService={createAccessService(authService)}>
      <PolicyAndFleetProvider policyId={policyId} ownerId={user?.ownerId ?? undefined}>
        <UiProvider>
          <ErrorBoundary>
            <Screen>
              <AuthenticatedRouter />
              {isFlocker && isNotLive && <PolicyDevTools policyId={policyId} />}
            </Screen>
          </ErrorBoundary>
        </UiProvider>
      </PolicyAndFleetProvider>
    </AccessProvider>
  )

  // Auth loading check
  if (!authService.isAuthenticated || authService.getAuthUser() === null) {
    return <FullPageLoader />
  }

  // Create claim submission path check
  if (authService.isAuthenticated && isCreateClaimSubmissionPath) {
    return <CreateClaimSubmission path={`${PATHS.CREATE_CLAIM_SUBMISSION}`} />
  }

  // Claim submission path check
  if (authService.isAuthenticated && isClaimSubmissionPath) {
    return renderClaimSubmission()
  }

  // Claim submission review path check
  if (authService.isAuthenticated && isClaimSubmissionReviewPath && policyId) {
    return renderClaimSubmissionReview()
  }

  // Temporary solution for Referrals page. Should review this when we have a better solution for the referral page.
  if (authService.isAuthenticated && window.location.pathname.startsWith(`/referrals`) && (isFlocker || isCarrier)) {
    return (
      <BrokerScreen>
        <Router>
          <ReferralsRouter path="referrals/*" />
        </Router>
      </BrokerScreen>
    )
  }

  // Broker view check (non-customer users without policy ID)
  if (!policyId && authService.isAuthenticated && user?.type !== `customer`) {
    return renderBrokerView()
  }

  // Standard authenticated customer view
  if (authService.isAuthenticated) {
    return renderCustomerView()
  }

  return null
}
